/** @module models */
'use strict';

var moment     = require('moment');
var validate   = require('validate.js');
var validators = require('helpers/validators.js');
var parseUtil  = require('helpers/parseUtil.js');
var log        = require('lib/log')('base-model');
var _proto     = _.clone(Parse.Object.prototype);

_.extend(validate.validators, validators);

/**
 * @name module:models.BaseModel
 * @constructor
 * @augments external:Parse.Object
 */
_.extend(Parse.Object.prototype,
/** @lends module:models.BaseModel */
{
    /**
     * Determines the deletion strategy to use for the inheriting model
     *
     * @type {boolean}
     * @default
     * @protected
     */
    softDelete: true,

    /**
     * Set of rules used to validate the model's attributes
     *
     * @type {object}
     * @default
     * @protected
     */
    constraints: {},

    /**
     * We have some use cases in which, on top of the constraints we
     * want to perform validation to some other attributes. So this would
     * be a subset of validation rules, used optionally
     *
     * @type {object}
     * @default
     * @protected
     */
    groupedConstraints: {},

    /**
     * Setter methods for attributes, their definition is optional.
     *
     * @type {object}
     * @default
     * @protected
     */
    setters: {},

    /**
     * Strips 'presence' constraints from the constraints object
     *
     * @instance
     * @private
     * @param {object} constraints - The object from where to strip the
     *  'presence' constraints
     * @param {object} attributes - The attributes for which we wish to
     *  strip 'presence' constraints.
     * @returns {object} A constraints object without any 'presence' rules.
     */
    _stripPresenceConstraints: function(constraints, attributes) {
        constraints = _.pick(constraints, _.keys(attributes));

        _.each(_.keys(constraints), function(attr) {
            var valids;

            if (typeof constraints[attr] === 'function') {
                constraints[attr] = constraints[attr](
                    attributes[attr],
                    attributes
                );
            }

            valids = _.reject(
                _.keys(constraints[attr]),
                function(constraint) { return constraint === 'presence'; }
            );

            constraints[attr] = _.pick(constraints[attr], valids);
        });

        return constraints;
    },

    /**
     * Whether or not the model is valid
     *
     * @instance
     * @param {object} options - A backbone style options object.
     * @returns {boolean} True if model is valid, false if not.
     */
    isValid: function(options) {
        return !this.validate(this.attributes, options);
    },

    /**
     * Whether or not the model was invalid. We check if validationError has
     * data in it.
     *
     * @instance
     * @returns {boolean} True if it was deemed valid, false if not.
     */
    wasValid: function() {
        return (
            this.validationError === false || _.isEmpty(this.validationError)
        );
    },

    /**
     * Validate a set of attributes against a defined set of constraints
     *
     * @instance
     * @param {object} attributes - The attributes to validate
     * @param {object} options - A backbone style options object
     * @returns {mixed} An error object or false if we encountered no errors
     */
    validate: function(attributes, options) {
        var self = this;
        var constraints = _.clone(self.constraints);
        var groupedConstraints;

        options = _.defaults(options || {}, {
            _set: false,
            _save: false,
            groupedConstraints: null,
            forcePresenceValidation: false,
            include: []
        });

        self.validationError = false;

        if (_.isObject(self.groupedConstraints[options.groupedConstraints])) {
            groupedConstraints = _.clone(
                self.groupedConstraints[options.groupedConstraints]
            );
        }

        if (_.isUndefined(attributes) || _.isDate(attributes.hiddenAt)) {
            return self.validationError;
        }

        if (
            options._save !== true &&
            options._set === true &&
            options.forcePresenceValidation === false
        ) {
            constraints = self._stripPresenceConstraints(
                constraints, attributes
            );

            if (_.isObject(groupedConstraints)) {
                groupedConstraints = self._stripPresenceConstraints(
                    groupedConstraints, attributes
                );
            }
        }

        self.validationError = _.extend(
            {},
            validate(attributes, constraints || {}),
            validate(attributes, groupedConstraints || {})
        );

        _.each(options.include || [], function(include) {
            var obj = attributes[include];

            if (_.isObject(obj) && _.isFunction(obj.validate)) {
                obj.validate(obj.attributes, options);

                self.validationError = _.extend(
                    self.validationError || {},
                    obj.validationError || {}
                );
            }
        });

        return _.isEmpty(self.validationError) ? false : {
            code: 1100,
            error: 'Validation error',
            errors: self.validationError
        };
    },

    /**
     * Saves the model
     * This is essentially the same as Parse.Object.save() but we
     * perform a validation on all attributes before saving to the server.
     *
     * @instance
     * @param {mixed} attr - Either an attribute name or an attributes object
     *  with key-value pairs.
     * @param {mixed} val - Either the value of the previous parameter or
     *  an options object.
     * @param {object} options - A backbone style options object
     * @returns {promise} A promise, resolved if the model was succesfully saved
     *  or reject if it wasn't
     */
    save: function(attr, val, options) {
        var self = this;
        var attrs = {};

        if (_.isNull(attr) || _.isUndefined(attr) || _.isObject(attr)) {
            attrs = attr;
            options = val;
        } else {
            attrs[attr] = val;
        }

        options = _.defaults(options || {}, {_save: true});

        if (
            !self._validate(self.attributes, options) &&
            !options.skipValidation
        ) {
            return Parse.Promise.error(self.validate(self.attributes, options));
        }

        return _proto.save.apply(self, [attrs, options]);
    },

    /**
     * Destroy the model
     *
     * @instance
     * @returns {promise} A resolved promise on a succesful delete, rejected
     *  otherwise
     */
    destroy: function() {
        var self = this;

        if (self.softDelete === true) {
            return self.save({
                hiddenAt: (new Date())
            }, {
                silent: true,
                success: function() {}
            }).then(function(model) {
                return Parse.Promise.as(model);
            });
        } else {
            return _proto.destroy.apply(self, arguments);
        }
    },

    /**
     * Smarter fetch method. Don't do a xhr request for new methods, just
     * rebuild default values
     *
     * @instance
     * @returns {promise} The promise of a synced model
     */
    fetch: function(options) {
        var self = this;
        var Constructor = self.constructor;
        var defaults = (new Constructor()).attributes;

        if (!self.isNew()) {
            return _proto.fetch.apply(self, arguments);
        }

        self._finishFetch(defaults, 200);

        return Parse.Promise.as(self)._thenRunCallbacks(options, self);
    },

    /**
     * Set a new value for an attribute. For any attributes with a setter
     * defined, we run the proposed value through the setter function first and
     * save whatever said function returns.
     *
     * @instance
     * @param {mixed} attribute - Either an attribute name or an object of
     *  key value pairs.
     * @param {mixed} value - Either an attribute value or an backbone-style
     *  options object
     * @param {object} object - If not undefined, a backbone-style options
     *  object.
     * @returns {models/BaseModel}
     */
    set: function(attribute, value, options) {
        var self = this;
        var changes = {};

        if (_.isString(attribute)) {
            changes[attribute] = value;
        } else if (_.isObject(value) && _.isUndefined(options)) {
            options = value;
        }

        if (_.isObject(attribute)) {
            changes = attribute;
        }

        options = _.defaults(options || {}, {
            _set: true
        });

        if (options._save === true) {
            options.silent = true;
        }

        _.each(_.keys(changes), function(attribute) {
            if (self.setters && _.isFunction(self.setters[attribute])) {
                log.debug(
                    'Setters should be deprecated for',
                    self.className, '.', attribute
                );

                changes[attribute] = self.setters[attribute](
                    changes[attribute]
                );
            }
        });

        return _proto.set.apply(self, [changes, options]);
    },

    /**
     * Returns the models' attributes in JSON format
     *
     * @instance
     * @param {object} options - A backbone-style options object
     * @returns {object} A JSON of the models' attributes
     */
    toJSON: function(options) {
        var self = this;

        options = _.defaults(options || {}, {
            deep: false
        });

        if (options.deep !== true) {
            return _proto.toJSON.apply(self);
        } else {
            return parseUtil.toJSON(self);
        }
    },

    /**
     * Returns a simple pointer object. Useful for specifying queries using
     * object literals
     *
     * @instance
     * @returns {object} A simple pointer object, with class name and id
     */
    toPointerObj: function() {
        if (this.isNew() && !this.id) {
            throw new Error(`Can't create pointer objects for new models`);
        }

        return {
            __type:    'Pointer',
            className: this.className,
            objectId:  this.id
        };
    },

    /**
     * Get dates in whatever specified format
     *
     * @instance
     * @param {string} attr - The attribute to format
     * @param {string} format - Format to use. Must be something momentjs
     *  understands
     * @returns {string} Formatted date
     */
    getFormatted: function(attr, format) {
        var value = this.get(attr);
        if (_.isDate(value)) {
            return moment(value).format(format);
        }
        return value;
    }
});

Parse.Object.setters = {
    boolean: function(value) {
        if (_.isBoolean(value)) {
            return value;
        }

        if (_.isString(value) && _.contains(['true', 'false'], value)) {
            return (value === 'true');
        }

        if (_.isString(value) && _.contains(['0', '1'], value)) {
            return (value === '1');
        }

        if (_.isNumber(value)) {
            return !!value;
        }

        return false;
    },

    string: function(value) {
        return (value && !_.isString(value) && _.isFunction(value.toString)) ?
            value.toString() : value;
    },

    integer: function(value) {
        return (!_.isNaN(parseInt(value, 10))) ?
            parseInt(value, 10) : value;
    },

    float: function(value) {
        value = (typeof value === 'string') ? value.replace(',', '') : value;
        return (!_.isNaN(parseFloat(value))) ? parseFloat(value) : value;
    },

    date: function(value) {
        if (_.isDate(value)) {
            return value;
        }
        var m;
        if (_.isString(value)) {
            m = moment(value);
        }
        if (_.isObject(value)) {
            m = moment(value.string, moment.format);
        }
        return m && m.isValid() ? m.toDate() : null;
    }
};

module.exports = Parse.Object;



// WEBPACK FOOTER //
// ./app/models/Base.js